import { Component, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../services/auth.service';
import { firstValueFrom } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { TranslationService } from '../../services/translation.service';
import { LanguageConfig } from '../../models/Translation';
import { StorageService } from '../../services/storage.service';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [RouterModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss',
})
export class SignupComponent {
  public signupForm!: FormGroup;
  currentLanguage: LanguageConfig;
  isAnonymousLoading = signal(false);
  isSignupBeforePurchase = signal(false);
  showPassword = signal(false);
  showVerifyPassword = signal(false);

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private toaster: ToastrService,
    private authService: AuthService,
    private storageService: StorageService,
    private translationService: TranslationService,
  ) {
    this.currentLanguage = this.translationService.getCurrentLanguageConfig();

    // Detect if we're coming from signup-before-purchase route
    this.isSignupBeforePurchase.set(this.router.url === '/signup-before-purchase');
    this.signupForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      verifyPassword: ['', [Validators.required, this.matchPassword('password')]],
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      legalAge: [false, Validators.pattern('true')],
      eulaOk: [false, Validators.pattern('true')],
    });
  }

  matchPassword(controlName: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      const passwd = control.parent?.get(controlName);
      if (control.value !== passwd?.value) {
        return { match: true };
      } else {
        return null;
      }
    };
  }

  async onSubmit() {
    if (!this.signupForm.valid) {
      return;
    }

    const email = this.signupForm.value.email;
    const passwd = this.signupForm.value.password;
    const firstname = this.signupForm.value.firstname;
    const lastname = this.signupForm.value.lastname;

    try {
      // Check if user has token and is anonymous
      const hasToken = this.storageService.getToken() !== null;
      const isAnonymous = this.storageService.isAnonymous();

      if (hasToken && isAnonymous) {
        // Use anonymous signup method
        let response = await firstValueFrom(this.authService.anonymousSignup(email, passwd, firstname, lastname));

        // Force refresh user info
        const user = await firstValueFrom(this.authService.forceUpdateUser());

        // Clear anonymous flag
        this.storageService.deleteAnonymous();

        // Check if there's a pending purchase to redirect to offers
        if (this.storageService.hasPendingPurchase()) {
          this.router.navigate(['/offers']);
        } else {
          this.router.navigate(['/onboarding/1']);
        }
      } else {
        // Normal signup flow
        let response = await firstValueFrom(this.authService.signup(email, passwd, firstname, lastname));
        let token = await firstValueFrom(this.authService.login(email, passwd));
        this.storageService.setToken(token.access_token);

        const user = await firstValueFrom(this.authService.getUserInfoOrRefreshIfNeeded());

        this.router.navigate(['/onboarding/1']);
      }
    } catch (error) {
      const errorMessage = this.translationService.instant('errors.network.serverError');
      const errorTitle = this.translationService.instant('common.labels.error');
      this.toaster.error(errorMessage, errorTitle);
    }
  }

  async onAnonymousLogin() {
    this.isAnonymousLoading.set(true);

    try {
      const response = await firstValueFrom(this.authService.anonymousLogin());
      this.storageService.setToken(response.access_token);
      this.storageService.setAnonymous(true);
      const user = await firstValueFrom(this.authService.getUserInfoOrRefreshIfNeeded());

      if (user.is_first_connection) {
        this.router.navigate(['/onboarding/1']);
      } else {
        this.router.navigate(['']);
      }
    } catch (error) {
      const errorMessage = this.translationService.instant('errors.network.serverError');
      const errorTitle = this.translationService.instant('common.labels.error');
      this.toaster.error(errorMessage, errorTitle);
    } finally {
      this.isAnonymousLoading.set(false);
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword.update((visible) => !visible);
  }

  toggleVerifyPasswordVisibility(): void {
    this.showVerifyPassword.update((visible) => !visible);
  }
}
